import ObjectID from 'bson-objectid';
import { FormField } from 'components/shared/form';
import { findIndex, min } from 'lodash';
import Form from 'mobx-react-form';
import { divide } from 'shared/utils/numberOperations';

export const taxesFormFields: FormField[] = [
  {
    name: 'incomeApproach',
    fields: [
      {
        name: 'taxes',
        fields: [
          {
            name: 'projected',
            fields: [
              {
                name: 'additionalTaxRates',
                type: 'array',
                value: [],
              },
              {
                name: 'equalizationRatio',
                type: 'number',
                value: 0,
              },
              {
                name: 'estimatedMarketValue',
                type: 'number',
                value: 0,
              },
              {
                name: 'taxableAssessedValue',
                type: 'number',
                value: 0,
              },
              {
                name: 'taxLiability',
                type: 'number',
                value: 0,
              },
            ],
          },
          {
            name: 'taxableAssessedValueItems',
            type: 'array',
            value: [],
          },
          {
            name: 'additionalTaxRates',
            type: 'array',
            value: [],
          },
          {
            name: 'specialAssessments',
            type: 'array',
            value: [],
          },
          {
            name: 'taxableAssessedValue',
            type: 'number',
            value: 0,
          },
          {
            name: 'taxRate',
            type: 'number',
            value: 0,
          },
          {
            name: 'taxLiability',
            type: 'number',
            value: 0,
          },
        ],
      },
    ],
  },
];

export const taxesFormHelpers = {
  addTaxableAssessedValueItem: (form: any) => {
    const itemsField = form.$('incomeApproach.taxes.taxableAssessedValueItems');
    const newItem = {
      id: new ObjectID().toHexString(),
      item: 'New Item',
      actual: 0,
      transitional: 0,
    };
    const currentItems = itemsField.value || [];
    currentItems.push(newItem);
    itemsField.set(currentItems);
  },
  deleteTaxableAssessedValueItem: (form: any, id: string) => {
    const itemsField = form.$('incomeApproach.taxes.taxableAssessedValueItems');
    const currentItems = itemsField.value || [];
    const index = findIndex(currentItems, (item: any) => item.id === id);
    const lastIndex = currentItems.length - 1;

    const newItems = currentItems.filter((_: Record<string, any>, i: number) => i !== index);

    itemsField.set(newItems);
    itemsField.del(lastIndex);
  },
  addAdditionalTaxRate: (form: any, projected = false) => {
    const additionalTaxRatesField = form.$(`incomeApproach.taxes.${projected ? 'projected.' : ''}additionalTaxRates`);
    const newTaxRate = {
      id: new ObjectID().toHexString(),
      taxRateName: 'Additional Tax Rate',
      taxRateValue: 0,
    };
    additionalTaxRatesField.add([newTaxRate]);
  },
  addSpecialAssessment: (form: any) => {
    const specialAssessmentsField = form.$('incomeApproach.taxes.specialAssessments');
    const newAssessment = {
      assessmentName: 'Special Assessment',
      assessmentValue: 0,
      id: new ObjectID().toHexString(),
    };
    specialAssessmentsField.add([newAssessment]);
  },

  deleteAdditionalTaxRate: (form: any, index: number, projected = false) => {
    const additionalTaxRatesField = form.$(`incomeApproach.taxes.${projected ? 'projected.' : ''}additionalTaxRates`);
    const additionalTaxRates = additionalTaxRatesField.value || [];
    const lastIndex = additionalTaxRates.length - 1;
    const newAdditionalTaxRates = additionalTaxRates.filter((_: Record<string, any>, i: number) => i !== index);
    additionalTaxRatesField.set(newAdditionalTaxRates);
    additionalTaxRatesField.del(lastIndex);
  },

  deleteSpecialAssessment: (form: any, index: number) => {
    const specialAssessmentsField = form.$('incomeApproach.taxes.specialAssessments');
    const specialAssessments = specialAssessmentsField.value || [];
    const lastIndex = specialAssessments.length - 1;
    const newSpecialAssessments = specialAssessments.filter((_: Record<string, any>, i: number) => i !== index);
    specialAssessmentsField.set(newSpecialAssessments);
    specialAssessmentsField.del(lastIndex);
  },

  calculateAdditionalTaxRateTotal: (form: any, projected = false) => {
    const additionalTaxRatesField = form.$(`incomeApproach.taxes.${projected ? 'projected.' : ''}additionalTaxRates`);
    const additionalTaxRates = additionalTaxRatesField.value || [];
    const additionalTaxRateTotal = additionalTaxRates?.reduce((acc: number, taxRate: any) => {
      return acc + (taxRate.taxRateValue || 0);
    }, 0);
    return additionalTaxRateTotal;
  },
  calculateSpecialAssessmentTotal: (form: any) => {
    const specialAssessmentsField = form.$('incomeApproach.taxes.specialAssessments');
    const specialAssessments = specialAssessmentsField.value || [];
    const specialAssessmentTotal = specialAssessments?.reduce((acc: number, assessment: any) => {
      return acc + (assessment.assessmentValue || 0);
    }, 0);
    return specialAssessmentTotal;
  },
  calculateTaxLiability: (form: any) => {
    const additionalTaxRateTotal = taxesFormHelpers.calculateAdditionalTaxRateTotal(form);
    const specialAssessmentTotal = taxesFormHelpers.calculateSpecialAssessmentTotal(form);

    const taxableAssessedValueField = form.$('incomeApproach.taxes.taxableAssessedValue');
    const taxableAssessedValue = taxableAssessedValueField.value;

    const taxRateField = form.$('incomeApproach.taxes.taxRate');
    const taxRate = taxRateField.value;

    const taxLiability = divide(taxRate + additionalTaxRateTotal, 100) * taxableAssessedValue + specialAssessmentTotal;
    const taxLiabilityField = form.$('incomeApproach.taxes.taxLiability');
    taxLiabilityField.set(taxLiability);
    return taxLiability;
  },

  calculateTaxableAssessedValue: (form: any) => {
    const itemsField = form.$('incomeApproach.taxes.taxableAssessedValueItems');
    const items = itemsField.value || [];
    const totalActual = items.reduce((acc: number, item: any) => {
      return acc + (item.actual || 0);
    }, 0);
    const totalTransitional = items.reduce((acc: number, item: any) => {
      return acc + (item.transitional || 0);
    }, 0);
    const total = min([totalActual, totalTransitional]);
    const taxableAssessedValueField = form.$('incomeApproach.taxes.taxableAssessedValue');
    taxableAssessedValueField.set(total);
    return total;
  },
  calculateImpliedTaxableAssessedValue: (form: Form) => {
    const estimatedMarketValueField = form.$('incomeApproach.taxes.projected.estimatedMarketValue');
    const estimatedMarketValue = estimatedMarketValueField.value;

    const equalizationRatioField = form.$('incomeApproach.taxes.projected.equalizationRatio');
    const equalizationRatio = equalizationRatioField.value;

    const impliedTaxableAssessedValue = divide(estimatedMarketValue * equalizationRatio, 100);
    const impliedTaxableAssessedValueField = form.$('incomeApproach.taxes.projected.taxableAssessedValue');
    impliedTaxableAssessedValueField.set(impliedTaxableAssessedValue);
  },
  calculateEqualizedMarketValueTaxLiability: (form: Form) => {
    const taxRateField = form.$('incomeApproach.taxes.taxRate');
    const taxRate = taxRateField.value;
    const additionalTaxRateTotal = taxesFormHelpers.calculateAdditionalTaxRateTotal(form, true);

    const impliedTaxableAssessedValueField = form.$('incomeApproach.taxes.projected.taxableAssessedValue');
    const impliedTaxableAssessedValue = impliedTaxableAssessedValueField.value;
    const projectedTaxLiabilityField = form.$('incomeApproach.taxes.projected.taxLiability');
    const taxLiability = impliedTaxableAssessedValue * divide(taxRate + additionalTaxRateTotal, 100);
    projectedTaxLiabilityField.set(taxLiability);
  },
};
